import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Check, X, Lightbulb, ArrowRight, Trophy, Volume2, VolumeX } from "lucide-react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAudio } from "../lib/useAudio";
import { log } from "../lib/logger";
import SpeakBack from "../components/SpeakBack";

const MODES = ["mcq", "typing"];

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

export default function Learn() {
  const { id } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState("mcq");
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong' | null
  const [reveal, setReveal] = useState(false);
  const [xpGained, setXpGained] = useState(0);
  const [done, setDone] = useState(false);
  const [hint, setHint] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const inputRef = useRef(null);
  const { pronounce } = useAudio(course?.language);
  const [autoplay, setAutoplay] = useState(() => {
    try { return localStorage.getItem("ll_autoplay") !== "0"; } catch { return true; }
  });
  const toggleAutoplay = () => {
    setAutoplay((v) => {
      const nv = !v;
      try { localStorage.setItem("ll_autoplay", nv ? "1" : "0"); } catch {}
      return nv;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        await api.post(`/courses/${id}/enroll`);
      } catch (err) {
        // idempotent enroll — safe to ignore duplicate errors
        log.debug("Enroll skipped:", err?.response?.status);
      }
      const [c, cs] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/courses/${id}/cards`),
      ]);
      setCourse(c.data);
      setCards(shuffle(cs.data));
    })();
  }, [id]);

  const card = cards[idx];

  // Build MCQ choices
  const choices = useMemo(() => {
    if (!card || cards.length < 2) return [];
    const others = shuffle(cards.filter((x) => x.id !== card.id)).slice(0, 3).map((x) => x.back);
    return shuffle([card.back, ...others]);
  }, [card, cards]);

  useEffect(() => {
    setAnswer("");
    setFeedback(null);
    setReveal(false);
    setHint(null);
    if (mode === "typing" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [idx, mode]);

  // Alternate modes each card
  useEffect(() => {
    setMode(MODES[idx % MODES.length]);
  }, [idx]);

  // Auto-play the target-language front the moment a new card appears.
  useEffect(() => {
    if (!card || !autoplay || done) return undefined;
    const t = setTimeout(() => {
      pronounce(card).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, autoplay, course?.language, done]);

  const norm = (s) => (s || "").trim().toLowerCase().replace(/[.,!?;:¡¿]/g, "").replace(/\s+/g, " ");

  const submitAnswer = async (given) => {
    if (feedback) return;
    const correct = norm(given) === norm(card.back) || card.back.toLowerCase().includes(norm(given));
    setFeedback(correct ? "correct" : "wrong");
    setReveal(true);
    try {
      const { data } = await api.post("/reviews", {
        card_id: card.id,
        course_id: id,
        quality: correct ? 5 : 2,
        mode,
      });
      setXpGained((v) => v + (data.xp_gain || 0));
    } catch (err) {
      log.error("Review submission failed", err);
    }
  };

  const next = () => {
    if (idx + 1 >= cards.length) {
      setDone(true);
      return;
    }
    setIdx((i) => i + 1);
  };

  const askHint = async () => {
    if (!card) return;
    setHintLoading(true);
    try {
      const { data } = await api.post("/ai/hint", { card_id: card.id });
      setHint(data);
    } catch {
      toast.error("Hint unavailable");
    } finally {
      setHintLoading(false);
    }
  };

  if (!card && !done) {
    return (
      <div className="min-h-screen grain-bg">
        <Nav />
        <div className="max-w-4xl mx-auto p-8 text-brand-navy">Loading cards...</div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen grain-bg" data-testid="learn-complete">
        <Nav />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="brutal-card p-12 bg-white">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-brand-yellow border-2 border-brand-ink shadow-brutal flex items-center justify-center mb-6">
              <Trophy className="w-10 h-10 text-brand-navy" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">Session complete!</h1>
            <p className="text-brand-navy/70 mt-2 text-lg">You earned <span className="font-bold text-brand-pink">+{xpGained} XP</span> 🎉</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/dashboard" className="brutal-btn-white">Back to home</Link>
              <button onClick={() => { setIdx(0); setDone(false); setCards(shuffle(cards)); setXpGained(0); }} className="brutal-btn-pink">
                Practice again <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg bg-brand-peach" data-testid="learn-page">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="brutal-badge bg-white mb-2">{course?.language} · {mode === "mcq" ? "Multiple choice" : "Type it"}</div>
            <div className="font-bold text-brand-navy/70">Card {idx + 1} of {cards.length}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAutoplay}
              data-testid="learn-autoplay-toggle"
              className={`brutal-badge ${autoplay ? "bg-brand-mint" : "bg-white"} text-xs`}
              title={autoplay ? "Auto-play is on. Click to mute." : "Auto-play is off. Click to unmute."}
              type="button"
            >
              {autoplay
                ? (<><Volume2 className="w-3.5 h-3.5" strokeWidth={3} /> Auto sound</>)
                : (<><VolumeX className="w-3.5 h-3.5" strokeWidth={3} /> Muted</>)}
            </button>
            <div className="flex items-center gap-2 brutal-badge bg-brand-yellow">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={3} /> +{xpGained} XP
            </div>
          </div>
        </div>

        <div className="h-3 border-2 border-brand-ink rounded-full bg-white overflow-hidden mb-8">
          <div className="h-full bg-brand-mint border-r-2 border-brand-ink" style={{ width: `${((idx) / cards.length) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`brutal-card p-10 md:p-12 bg-white ${feedback === "wrong" ? "animate-shake" : ""}`}
          >
            <div className="text-sm font-bold uppercase text-brand-navy/50 tracking-wider mb-2">In {course?.language || "the target language"}</div>
            <div className="flex items-center gap-3">
              <div className="font-display text-4xl md:text-6xl font-bold text-brand-navy">{card.front}</div>
              <button
                type="button"
                onClick={() => pronounce(card)}
                data-testid="learn-front-audio-btn"
                className="brutal-btn-yellow p-3 rounded-full shrink-0"
                title="Play pronunciation"
              >
                <Volume2 className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>

            {reveal && (
              <div className="mt-6 pt-6 border-t-2 border-dashed border-brand-ink/30">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold uppercase text-brand-navy/50 tracking-wider">Means (English)</div>
                    <div className="font-display text-3xl font-bold text-brand-pink">{card.back}</div>
                  </div>
                  <button
                    onClick={() => pronounce(card)}
                    data-testid="learn-audio-btn"
                    className="brutal-btn-yellow p-3 rounded-full"
                    title="Play pronunciation"
                    type="button"
                  >
                    <Volume2 className="w-5 h-5" strokeWidth={3} />
                  </button>
                </div>
                {card.example && <div className="mt-3 text-brand-navy/70 italic">"{card.example}"</div>}
                <div className="mt-4">
                  <SpeakBack
                    cardId={card.id}
                    onPlayModel={() => pronounce(card)}
                    onScore={(r) => setXpGained((v) => v + (r.xp_gain || 0))}
                  />
                </div>
              </div>
            )}

            {hint && (
              <div className="mt-6 brutal-card-sm p-4 bg-brand-yellow">
                <div className="text-xs font-bold uppercase text-brand-navy/70 mb-1">Lumi's hint</div>
                {hint.mnemonic && <div className="text-brand-navy font-semibold">{hint.mnemonic}</div>}
                {hint.example && <div className="text-brand-navy/80 italic mt-2">"{hint.example}"</div>}
                {hint.translation && <div className="text-brand-navy/60 text-sm mt-1">{hint.translation}</div>}
              </div>
            )}

            {!feedback && mode === "mcq" && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {choices.map((c, i) => (
                  <button
                    key={c + i}
                    onClick={() => submitAnswer(c)}
                    data-testid={`learn-choice-${i}`}
                    className="brutal-btn-white text-left justify-start text-lg py-4"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {!feedback && mode === "typing" && (
              <form
                onSubmit={(e) => { e.preventDefault(); submitAnswer(answer); }}
                className="mt-8 flex flex-col sm:flex-row gap-3"
              >
                <input
                  ref={inputRef}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  data-testid="learn-typing-input"
                  className="brutal-input flex-1 text-lg"
                  placeholder="Type your answer..."
                />
                <button type="submit" data-testid="learn-typing-submit" className="brutal-btn-pink text-lg">
                  Check <ArrowRight className="w-5 h-5" strokeWidth={3} />
                </button>
              </form>
            )}

            {feedback && (
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div
                  data-testid="learn-feedback"
                  className={`brutal-badge text-base py-2 px-4 ${feedback === "correct" ? "bg-brand-mint" : "bg-brand-pink text-white"}`}
                >
                  {feedback === "correct" ? <><Check className="w-4 h-4" strokeWidth={3} /> Nice!</> : <><X className="w-4 h-4" strokeWidth={3} /> Not quite</>}
                </div>
                <button onClick={next} data-testid="learn-next-btn" className="brutal-btn-navy text-lg">
                  Continue <ArrowRight className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>
            )}

            {!feedback && (
              <button
                onClick={askHint}
                disabled={hintLoading}
                data-testid="learn-hint-btn"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-navy/70 hover:text-brand-pink"
              >
                <Lightbulb className="w-4 h-4" strokeWidth={3} /> {hintLoading ? "Thinking..." : "Give me a hint"}
              </button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
